import { countTokenUses, hexToHslParts, tokenInClasses, tokenPromptLines } from '../../preview-bridge/designTokens';
import type { HotEditSelection } from '../../stores/hotEdit';
import { rowNoun } from './panelModel';

/**
 * The two questions the editor asks itself before writing a change (Prototype Editor UX plan,
 * step 4), so the founder does not have to know how the prototype is built.
 *
 * A colour picked on a button that uses `bg-primary` could mean "this button" or "our brand is
 * teal now"; a size picked on one card in a list could mean "this card" or "all of them". The
 * old editor guessed, silently, and was wrong half the time. Now the change shows at once and
 * the editor asks which it was - once, with the answer remembered.
 *
 * Pure: what to ask and how to word it, with no store and no DOM.
 */
export type SmartPrompt =
  | {
      kind: 'token';
      title: string;
      body: string;
      /** `--primary` and the value to put in it, if the founder says everywhere. */
      token: string;
      hsl: string;
      hex: string;
      property: 'bg' | 'text';
      everywhere: string;
      onlyHere: string;
    }
  | {
      kind: 'repeat';
      title: string;
      body: string;
      count: number;
      all: string;
      onlyHere: string;
    };

/** What the founder just changed, in the words the question uses. */
export type StyleChange = 'colour' | 'look';

/**
 * The colour question: only when the element takes this colour from the design system today, and
 * only when the founder picked something else. A custom colour on an element that already had a
 * custom colour is nobody's brand decision.
 */
export function tokenPromptFor(args: {
  selection: HotEditSelection;
  hex: string;
  property: 'bg' | 'text';
  files: Record<string, string>;
}): SmartPrompt | null {
  const token = tokenInClasses(args.selection.className, args.property);
  if (!token) return null;
  const hsl = hexToHslParts(args.hex);
  if (!hsl) return null;
  const lines = tokenPromptLines(token, countTokenUses(args.files, token));
  return {
    kind: 'token',
    title: lines.title,
    body: lines.body,
    token,
    hsl,
    hex: args.hex,
    property: args.property,
    // The title already names the colour, so the buttons stay short enough to sit side by side.
    everywhere: 'Everywhere',
    onlyHere: 'Only this one',
  };
}

/**
 * The repeat question: only for a row of a list with more than one row, and only until the
 * founder has answered it for this prototype.
 */
export function repeatPromptFor(args: {
  selection: HotEditSelection;
  change: StyleChange;
  remembered?: 'item' | 'all';
}): SmartPrompt | null {
  if (args.remembered) return null;
  const row = args.selection.listRow;
  if (!row || row.count < 2) return null;
  const plural = rowNoun(args.selection);
  return {
    kind: 'repeat',
    title: `Apply to all ${row.count} ${plural}?`,
    body:
      args.change === 'colour'
        ? `This one is ${row.count === 2 ? 'one of two' : `one of ${row.count}`} that look the same.`
        : `The other ${row.count - 1} are built from the same design.`,
    count: row.count,
    all: `All ${row.count}`,
    onlyHere: 'Just this one',
  };
}
